/*
  BYLT bundle stock guard (Fast Bundle).

  Problem: when a bundle slot is set to a size with less stock than the bundle needs
  (e.g. 3 x Black / S with only 1 left), Fast Bundle adds what Shopify accepts and
  silently drops the rest, so the shopper ends up with 1 item and no bundle discount.
  Fast Bundle does the real add itself, so watching /cart/add requests can't catch it.

  Fix: catch the Add to cart click (and form submit) before Fast Bundle sees it, read
  the sizes chosen in the widget, check them against live stock, and only let the click
  through when every size has enough stock. Stock comes from
  /products/<handle>?view=bundle-stock (templates/product.bundle-stock.liquid).

  Works for both widget layouts:
  - Fixed bundles: one row per item (.rbr-sbap_item) with the chosen size shown.
  - Mix and match: a "selected" list with a count per size.

  If anything can't be read (widget markup changed, request fails), the click goes
  through as normal, so this never stops a valid add.
*/
(function () {
  'use strict';

  var DEBUG = false;
  var BUNDLE_ROOT = '#rbr-container-element-true';
  var ADD_BUTTON = 'button.add-to-cart, button[name="add"]';
  var STOCK_VIEW = 'bundle-stock';
  var CACHE_MS = 30000;

  var stockCache = {};
  var bypass = false;

  function dlog() {
    if (!DEBUG) return;
    // eslint-disable-next-line no-console
    console.log.apply(console, ['[Bundle stock guard]'].concat([].slice.call(arguments)));
  }

  function bundleRoot() {
    return document.querySelector(BUNDLE_ROOT);
  }

  // ---------- Error message under the Add to cart button ----------

  function errorEl() {
    var wrapper = document.querySelector('.quantity-submit-row__submit');
    if (!wrapper) return null;
    var el = wrapper.querySelector('.js-form-error[role="alert"]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'js-form-error';
      el.setAttribute('role', 'alert');
      el.hidden = true;
      wrapper.appendChild(el);
    }
    return el;
  }

  function placeErrorBelowButton() {
    var wrapper = document.querySelector('.quantity-submit-row__submit');
    if (!wrapper) return;
    var el = wrapper.querySelector('.js-form-error[role="alert"]');
    var btn =
      wrapper.querySelector('button.add-to-cart.rbr') ||
      wrapper.querySelector('button.add-to-cart') ||
      wrapper.querySelector('button[name="add"][type="submit"]');
    if (el && btn && btn.nextElementSibling !== el) {
      btn.insertAdjacentElement('afterend', el);
    }
  }

  function showError(msg) {
    var el = errorEl();
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    placeErrorBelowButton();
  }

  function clearError() {
    var el = errorEl();
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  // ---------- Read what the shopper picked in the widget ----------

  function handleFrom(scope) {
    var a = scope && scope.querySelector('a[href*="/products/"]');
    if (!a) return null;
    try {
      var path = new URL(a.href, location.origin).pathname;
      var m = path.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function text(el) {
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  // Returns [{ handle, variantTitle, qty }] or null if the widget can't be read.
  function readSelections(root) {
    var picks = [];

    // Fixed bundles: one row per item, size shown in the custom select box.
    var rows = root.querySelectorAll('.rbr-sbap_item');
    for (var i = 0; i < rows.length; i++) {
      var handle = handleFrom(rows[i]);
      var size = text(rows[i].querySelector('[class*="selectBoxCustom_placeholder"]'));
      if (!handle || !size) return null;
      picks.push({ handle: handle, variantTitle: size, qty: 1 });
    }

    // Mix and match: a selected list per product with a count per size.
    var listed = root.querySelectorAll('[class*="SelectedVariantsList_item"]');
    for (var j = 0; j < listed.length; j++) {
      var item = listed[j];
      var container = item.closest('[class*="style_container"]') || item.parentElement;
      var h = handleFrom(container);
      var t = text(item.querySelector('[class*="SelectedVariantsList_title"]'));
      var c = parseInt(text(item.querySelector('[class*="SelectedVariantsList_count"]')), 10);
      if (!h || !t || !c) return null;
      picks.push({ handle: h, variantTitle: t, qty: c });
    }

    return picks.length ? picks : null;
  }

  // ---------- Stock lookup ----------

  function getStock(handle) {
    var hit = stockCache[handle];
    if (hit && Date.now() - hit.at < CACHE_MS) return Promise.resolve(hit.data);

    var url = '/products/' + encodeURIComponent(handle) + '?view=' + STOCK_VIEW + '&_=' + Date.now();
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('stock ' + res.status);
        return res.text();
      })
      .then(function (body) {
        var data = JSON.parse(body);
        stockCache[handle] = { at: Date.now(), data: data };
        return data;
      });
  }

  // Resolves to an error message, or '' when everything is in stock.
  function checkStock(picks) {
    var need = {};
    var order = [];
    picks.forEach(function (p) {
      var key = p.handle + '|' + p.variantTitle;
      if (!need[key]) {
        need[key] = { handle: p.handle, variantTitle: p.variantTitle, qty: 0 };
        order.push(key);
      }
      need[key].qty += p.qty;
    });

    var handles = [];
    order.forEach(function (k) {
      if (handles.indexOf(need[k].handle) === -1) handles.push(need[k].handle);
    });

    return Promise.all(handles.map(getStock)).then(function (results) {
      var byHandle = {};
      results.forEach(function (r, idx) { byHandle[handles[idx]] = r; });

      for (var i = 0; i < order.length; i++) {
        var n = need[order[i]];
        var product = byHandle[n.handle];
        if (!product || !product.variants) continue;

        var v = null;
        for (var k = 0; k < product.variants.length; k++) {
          if (product.variants[k].title === n.variantTitle) { v = product.variants[k]; break; }
        }
        if (!v || !v.deny || v.qty === null) continue; // untracked or oversell allowed

        var left = Math.max(0, v.qty);
        if (n.qty > left) {
          var name = (product.title || '') + ' (' + n.variantTitle + ')';
          if (left === 0) {
            return name + ' is sold out. Please choose a different size or colour to complete your bundle.';
          }
          return 'Only ' + left + ' left in ' + name + ', and your bundle has ' + n.qty +
            '. Please choose a different size or colour for the rest to complete your bundle.';
        }
      }
      return '';
    });
  }

  // ---------- Intercept Add to cart ----------

  function guard(event, resume) {
    if (bypass) return;
    var root = bundleRoot();
    if (!root) return;

    var picks = readSelections(root);
    if (!picks) {
      dlog('Could not read selections, letting add through');
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    clearError();

    checkStock(picks)
      .then(function (msg) {
        if (msg) {
          dlog('Blocked:', msg);
          showError(msg);
          return;
        }
        resume();
      })
      .catch(function (err) {
        dlog('Stock check failed, letting add through', err);
        resume();
      });
  }

  function resumeWith(fn) {
    return function () {
      bypass = true;
      try { fn(); } finally {
        setTimeout(function () { bypass = false; }, 0);
      }
    };
  }

  // Clicks on the main Add to cart button (Fast Bundle hooks the click).
  window.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest(ADD_BUTTON);
    if (!btn || !bundleRoot()) return;
    guard(event, resumeWith(function () { btn.click(); }));
  }, true);

  // Form submits (sticky add to cart, Enter key) on bundle pages.
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1 || !bundleRoot()) return;
    var submitter = event.submitter;
    guard(event, resumeWith(function () {
      if (form.requestSubmit) form.requestSubmit(submitter || undefined);
      else form.submit();
    }));
  }, true);

  // Keep the message under the button if the widget re-renders.
  function initErrorPlacementWatcher() {
    placeErrorBelowButton();
    var root = bundleRoot() || document.querySelector('.quantity-submit-row__submit');
    if (root) {
      new MutationObserver(placeErrorBelowButton).observe(root, { childList: true, subtree: true });
    }
    window.addEventListener('load', placeErrorBelowButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initErrorPlacementWatcher);
  } else {
    initErrorPlacementWatcher();
  }

  dlog('Loaded');
})();
